import {
  DeleteOutlined,
  EditOutlined,
  TranslationOutlined,
} from "@ant-design/icons";
import { Button, Form, Input, message, Modal, Select, Space, Tabs } from "antd";
import React, { useEffect, useState, useCallback } from "react";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import Toasts from "../../../core/common/toast";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import {
  addLanguageGroup,
  addLanguageVersion,
  deleteLanguageVersion,
  getLanguageGroups,
  getLanguages,
  getLanguageVersions,
  setDefaultLanguageVersion,
  updateLanguageStatus,
  updateLanguageVersion,
  addLanguages
} from "../../../core/data/redux/languageSlice";
import { all_routes } from "../../router/all_routes";
import { fetchAll } from "../../../core/data/redux/rolePermissionsSlice";
import { isSupportViewer } from "../../../utils/permissionUtils";

interface LanguageString {
  key: string;
  value: string;
}

interface Language {
  id: string;
  name: string;
  code: string;
  group: string;
  status: boolean;
  strings?: LanguageString[];
}

interface LanguageGroup {
  id: string;
  group_name: string;
  status: boolean;
  languages?: Language[];
}

interface LanguageVersion {
  id: string;
  version: string; // Main version field from API
  version_name?: string; // Optional display name
  language_id: string;
  language_name: string;
  language_code?: string;
  created_by: string;
  status: boolean;
  created_date: string;
  is_default: boolean;
  description?: string; // Optional description
}

const LanguageList: React.FC = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [listType, setListType] = useState<string>("languages");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");

  // Redux state
  const languagesState = useSelector((state: any) => state.languageState);

  const languageData = languagesState?.data || null;
  const languageGroupsData = languagesState?.groupsData || null;
  const languageVersionsData = languagesState?.versionsData || null;
  const addLanguageGroupData = languagesState?.addLanguageGroupData || null;
  const updateLanguageStatusData =
    languagesState?.updateLanguageStatusData || null;
  const addLanguageVersionData = languagesState?.addLanguageVersionData || null;
  const updateLanguageVersionData =
    languagesState?.updateLanguageVersionData || null;
  const deleteLanguageVersionData =
    languagesState?.deleteLanguageVersionData || null;
  const setDefaultVersionData = languagesState?.setDefaultVersionData || null;

  const fetchLanguageRecords = (page = 1, limit = 10, query = "") => {
    console.log("🚀 Fetching languages...");
    dispatch(getLanguages({ page, limit, query }));
  };

  const fetchLanguageGroups = (page = 1, limit = 10, query = "") => {
    dispatch(getLanguageGroups({ page, limit, query }));
  };

  const fetchLanguageVersions = (page = 1, limit = 10, query = "", language = "") => {
    dispatch(getLanguageVersions({ page, limit, query, version: query, language }));
  };

  const debouncedFetch = useCallback(
    debounce((type: string, p: any, q: string) => {
      if (type === "languages") fetchLanguageRecords(p.current, p.pageSize, q);
      else if (type === "groups") fetchLanguageGroups(p.current, p.pageSize, q);
      else if (type === "versions") fetchLanguageVersions(p.current, p.pageSize, q);
    }, 500),
    []
  );

  const handleSearch = (value: string) => {
    setSearchText(value);
    const resetPagination = { ...pagination, current: 1 };
    setPagination(resetPagination);

    if (value === "") {
      debouncedFetch.cancel();
      if (listType === "languages") fetchLanguageRecords(1, pagination.pageSize, "");
      else if (listType === "groups") fetchLanguageGroups(1, pagination.pageSize, "");
      else if (listType === "versions") fetchLanguageVersions(1, pagination.pageSize, "");
    } else {
      debouncedFetch(listType, resetPagination, value);
    }
  };

  // Initial data fetch
  useEffect(() => {
    setSearchText(""); // Clear search when switching tabs
    if (listType === "languages") {
      fetchLanguageRecords(1, pagination.pageSize, "");
    } else if (listType === "groups") {
      fetchLanguageGroups(1, pagination.pageSize, "");
    } else if (listType === "versions") {
      fetchLanguageVersions(1, pagination.pageSize, "");
      if (!languageData) {
        fetchLanguageRecords(1, 100, "");
      }
    }
  }, [listType]);

  // Update pagination when data changes
  useEffect(() => {
    if (listType === "languages" && languageData) {
      const total = languageData.result?.length || languageData.length || 0;
      setPagination((prev) => ({
        ...prev,
        total: total,
      }));
    } else if (listType === "groups" && languageGroupsData) {
      const total =
        languageGroupsData.result?.length || languageGroupsData.length || 0;
      setPagination((prev) => ({
        ...prev,
        total: total,
      }));
    } else if (listType === "versions" && languageVersionsData) {
      const total =
        languageVersionsData.result?.length || languageVersionsData.length || 0;
      setPagination((prev) => ({
        ...prev,
        total: total,
      }));
    }
  }, [languageData, languageGroupsData, languageVersionsData, listType]);

  // Handle success responses
  useEffect(() => {
    if (addLanguageGroupData?.status === 201) {
      setIsModalVisible(false);
      form.resetFields();
      fetchLanguageGroups();
    }
  }, [addLanguageGroupData]);


  useEffect(() => {
    if (languagesState.loading === 'fulfilled' && languagesState.action === 'add-lang') {
      setIsModalVisible(false);
      form.resetFields();
      dispatch(getLanguages({}));
      toast.success('Language saved successfully');
    }
  }, [languagesState.loading]);


  useEffect(() => {
    if (addLanguageGroupData?.status === 201) {
      setIsModalVisible(false);
      form.resetFields();
      fetchLanguageGroups();
    }
  }, [addLanguageGroupData]);



  useEffect(() => {
    if (updateLanguageStatusData?.status === 200) {
      fetchLanguageRecords();
    }
  }, [updateLanguageStatusData]);

  // Handle language version operations (following existing pattern)
  useEffect(() => {
    if (
      addLanguageVersionData?.status === 201 ||
      addLanguageVersionData?.status === 200
    ) {
      setIsModalVisible(false);
      form.resetFields();
      setEditingId(null);
      fetchLanguageVersions();
    }
  }, [addLanguageVersionData]);

  useEffect(() => {
    if (updateLanguageVersionData?.status === 200) {
      setIsModalVisible(false);
      form.resetFields();
      setEditingId(null);
      fetchLanguageVersions();
    }
  }, [updateLanguageVersionData]);

  useEffect(() => {
    if (deleteLanguageVersionData?.status === 200) {
      fetchLanguageVersions();
    }
  }, [deleteLanguageVersionData]);

  useEffect(() => {
    if (setDefaultVersionData?.status === 200) {
      fetchLanguageVersions();
    }
  }, [setDefaultVersionData]);

  // Table columns for languages
  const languageColumns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      render: (_: any, __: any, index: number) => (
        <div>{(pagination.current - 1) * pagination.pageSize + index + 1}</div>
      ),
      sorter: (a: Language, b: Language) => a.id.localeCompare(b.id),
    },
    {
      title: "Language Name",
      dataIndex: "name",
      sorter: (a: Language, b: Language) => a.name.localeCompare(b.name),
    },
    {
      title: "Language Code",
      dataIndex: "code",
      sorter: (a: Language, b: Language) => a.code.localeCompare(b.code),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_: any, record: Language) => (
        <span
          className={`badge ${record.status ? "badge-success" : "badge-danger"
            } d-inline-flex align-items-center badge-xs`}
          onClick={() => !isSupportViewer() && handleChangeStatus(record)}
          style={{ cursor: isSupportViewer() ? "default" : "pointer" }}
        >
          <i className="ti ti-point-filled me-1" />
          {record.status ? "Active" : "Inactive"}
        </span>
      ),
      sorter: (a: Language, b: Language) => Number(a.status) - Number(b.status),
    },
    ...(!isSupportViewer()
      ? [
          {
            title: "Actions",
            dataIndex: "actions",
            render: (_: any, record: Language) => (
              <Space>
                <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record.id)}
                />
                <Button
                  icon={<TranslationOutlined />}
                  onClick={() =>
                    navigate("/language-phrases", {
                      state: {
                        editingLanguage: record,
                      },
                    })
                  }
                  title="Update Strings"
                />
              </Space>
            ),
          },
        ]
      : []),
  ];

  // Table columns for language groups
  const languageGroupsColumns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      render: (_: any, __: any, index: number) => (
        <div>{(pagination.current - 1) * pagination.pageSize + index + 1}</div>
      ),
      sorter: (a: LanguageGroup, b: LanguageGroup) => a.id.localeCompare(b.id),
    },
    {
      title: "Language Group Name",
      dataIndex: "group_name",
      sorter: (a: LanguageGroup, b: LanguageGroup) =>
        a.group_name.localeCompare(b.group_name),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_: any, record: LanguageGroup) => (
        <span
          className={`badge ${record.status ? "badge-success" : "badge-danger"
            } d-inline-flex align-items-center badge-xs`}
        >
          <i className="ti ti-point-filled me-1" />
          {record.status ? "Active" : "Inactive"}
        </span>
      ),
      sorter: (a: LanguageGroup, b: LanguageGroup) =>
        Number(a.status) - Number(b.status),
    },
    ...(!isSupportViewer()
      ? [
          {
            title: "Actions",
            dataIndex: "actions",
            render: (_: any, record: LanguageGroup) => (
              <Space>
                <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record.id)}
                />
              </Space>
            ),
          },
        ]
      : []),
  ];

  // Table columns for language versions
  const languageVersionsColumns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      render: (_: any, __: any, index: number) => (
        <div>{(pagination.current - 1) * pagination.pageSize + index + 1}</div>
      ),
      sorter: (a: LanguageVersion, b: LanguageVersion) =>
        a.id.localeCompare(b.id),
    },
    {
      title: "Version",
      dataIndex: "version",
      render: (version: string) => (
        <div style={{ fontWeight: 500 }}>{version}</div>
      ),
      sorter: (a: LanguageVersion, b: LanguageVersion) =>
        (a.version || "").localeCompare(b.version || ""),
    },
    {
      title: "Language",
      dataIndex: "language",
      render: (language: any) => (
        <div style={{ fontWeight: 500 }}>{language?.name || "N/A"}</div>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (date: string) => (
        <div style={{ fontSize: "12px" }}>
          {date
            ? new Date(date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
            : "N/A"}
        </div>
      ),
      sorter: (a: LanguageVersion, b: LanguageVersion) =>
        new Date(a.created_date || 0).getTime() -
        new Date(b.created_date || 0).getTime(),
    },
    {
      title: "Created By",
      dataIndex: "created_by",
      render: (createdBy: string) => (
        <span
          style={{
            backgroundColor: "#f0f0f0",
            padding: "2px 8px",
            borderRadius: "4px",
            fontSize: "12px",
          }}
        >
          {createdBy}
        </span>
      ),
      sorter: (a: LanguageVersion, b: LanguageVersion) =>
        (a.created_by || "").localeCompare(b.created_by || ""),
    },
  ];

  // Event handlers
  const handleAdd = () => {
    setEditingId(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record: Language | LanguageGroup | LanguageVersion) => {
    setEditingId(record.id);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleDelete = (id: string) => {
    let itemType = "item";
    if (listType === "languages") {
      itemType = "language";
    } else if (listType === "groups") {
      itemType = "language group";
    } else if (listType === "versions") {
      itemType = "language version";
    }

    Modal.confirm({
      title: `Are you sure you want to delete this ${itemType}?`,
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: () => {
        if (listType === "versions") {
          // Call actual API for language versions
          dispatch(deleteLanguageVersion({ id }));
        } else {
          // For now, just show a message since other delete APIs aren't implemented
          message.info(
            `Delete ${itemType} functionality will be implemented with API integration`
          );
        }
      },
    });
  };

  const handleChangeStatus = (record: Language) => {
    dispatch(updateLanguageStatus({ id: record.id, isActive: !record.status }));
  };

  const handleSetDefault = (record: LanguageVersion) => {
    dispatch(setDefaultLanguageVersion({ id: record.id }));
  };

  const handleVersionStatusChange = (record: LanguageVersion) => {
    // For now, show a message since status update API might not be implemented
    Modal.confirm({
      title: `${record.status ? "Deactivate" : "Activate"} Version`,
      content: `Are you sure you want to ${record.status ? "deactivate" : "activate"
        } this version?`,
      okText: "Yes",
      cancelText: "No",
      onOk: () => {
        message.info(
          "Version status update functionality will be implemented with API integration"
        );
        // When API is ready: dispatch(updateLanguageVersionStatus({ id: record.id, status: !record.status }));
      },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (listType === "languages") {
        console.log('values>>', editingId)
        // For now, just show message since add language API isn't implemented
        // message.info(
        //   "Add language functionality will be implemented with API integration"
        // );
        // setIsModalVisible(false);
        // form.resetFields();
        dispatch(addLanguages({ name: values.name, code: values.code, id: editingId || undefined }))

      } else if (listType === "groups") {
        dispatch(addLanguageGroup({ ...values, status: true }));
      } else if (listType === "versions") {
        if (editingId) {
          // Update existing language version
          const updatePayload = {
            id: editingId,
            language_id: values.language_id,
            version: values.version,
            created_by: "admin", // Default for updates
          };
          dispatch(updateLanguageVersion(updatePayload));
        } else {
          // Add new language version - send ONLY the required API fields
          const apiPayload = {
            language_id: values.language_id,
            version: values.version,
            created_by: "admin", // Default creator
          };

          dispatch(addLanguageVersion(apiPayload));
        }
      }
    } catch (error) {
      console.error("❌ Form validation error:", error);
    }
  };

  const handlePagination = (paginationConfig: any) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      total: paginationConfig.total,
    });
    if (listType === "languages") fetchLanguageRecords(paginationConfig.current, paginationConfig.pageSize, searchText);
    else if (listType === "groups") fetchLanguageGroups(paginationConfig.current, paginationConfig.pageSize, searchText);
    else if (listType === "versions") fetchLanguageVersions(paginationConfig.current, paginationConfig.pageSize, searchText);
  };

  // Determine what data to show in the table
  const getTableData = () => {
    if (listType === "languages") {
      const data = languageData?.result || languageData?.data || [];
      return data;
    } else if (listType === "groups") {
      const data =
        languageGroupsData?.result ||
        languageGroupsData?.data ||
        languageGroupsData ||
        [];
      return data;
    } else if (listType === "versions") {
      // Use actual API data from Redux state (no mock data)
      const data =
        languageVersionsData?.result ||
        languageVersionsData?.data ||
        languageVersionsData ||
        [];
      return data;
    }
    return [];
  };

  const tableData = getTableData();

  return (
    <div className="page-wrapper">
      <Toasts
        show={languagesState.loading === "rejected"}
        message={languagesState.error}
        title={"Failed"}
        type={"danger"}
      />
      <Toasts
        show={addLanguageGroupData?.status === 201}
        message={addLanguageGroupData?.message || "Language group added successfully"}
        title={"Success"}
        type={"success"}
      />
      <Toasts
        show={updateLanguageStatusData?.status === 200}
        message={updateLanguageStatusData?.message || "Status updated successfully"}
        title={"Success"}
        type={"success"}
      />
      <Toasts
        show={
          addLanguageVersionData?.status === 201 ||
          addLanguageVersionData?.status === 200
        }
        message={
          addLanguageVersionData?.message ||
          "Language version created successfully!"
        }
        title={"Success"}
        type={"success"}
      />
      <Toasts
        show={updateLanguageVersionData?.status === 200}
        message={
          updateLanguageVersionData?.message ||
          "Language version updated successfully!"
        }
        title={"Success"}
        type={"success"}
      />
      <Toasts
        show={deleteLanguageVersionData?.status === 200}
        message={
          deleteLanguageVersionData?.message ||
          "Language version deleted successfully!"
        }
        title={"Success"}
        type={"success"}
      />
      <Toasts
        show={setDefaultVersionData?.status === 200}
        message={setDefaultVersionData?.message || "Default version set successfully!"}
        title={"Success"}
        type={"success"}
      />

      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">
              {listType === "languages"
                ? "Languages"
                : listType === "groups"
                  ? "Language Groups"
                  : "Language Versions"}
            </h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="#">Languages</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {listType === "languages"
                    ? "Languages List"
                    : listType === "groups"
                      ? "Language Groups List"
                      : "Language Versions List"}
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            {!isSupportViewer() && (
              <Button
                type="primary"
                onClick={handleAdd}
                icon={<i className="ti ti-square-rounded-plus me-2" />}
                style={{
                  backgroundColor: "#c0282c",
                  border: "1px solid #c0282c",
                  fontWeight: "500",
                }}
              >
                {listType === "languages"
                  ? "Add Language"
                  : listType === "groups"
                    ? "Add Language Group"
                    : "Add Language Version"}
              </Button>
            )}
          </div>
        </div>

        {/* Summary Stats Cards */}
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <Tabs
              activeKey={listType}
              onChange={(key) => setListType(key)}
              items={[
                {
                  key: 'languages',
                  label: 'Languages',
                },
                {
                  key: 'groups',
                  label: 'Groups',
                },
                {
                  key: 'versions',
                  label: 'Versions',
                },
              ]}
            />
          </div>
          <div className="card-body p-0 py-3">
            <Table
              columns={
                listType === "languages"
                  ? languageColumns
                  : listType === "groups"
                    ? languageGroupsColumns
                    : languageVersionsColumns
              }
              dataSource={tableData}
              Selection={false}
              paginationConfig={pagination}
              handlePagination={handlePagination}
              loading={languagesState.loading === "pending"}
              handleSearch={handleSearch}
              searchValue={searchText}
            />
          </div>
        </div>

        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <i className={`ti ti-${editingId ? "edit" : "plus"}`} />
              {listType === "languages"
                ? editingId
                  ? "Edit Language"
                  : "Add Language"
                : listType === "groups"
                  ? editingId
                    ? "Edit Language Group"
                    : "Add Language Group"
                  : editingId
                    ? "Edit Language Version"
                    : "Add Language Version"}
            </div>
          }
          open={isModalVisible}
          onOk={handleModalOk}
          onCancel={() => {
            setIsModalVisible(false);
            form.resetFields();
            setEditingId(null);
          }}
          confirmLoading={languagesState.loading === "pending"}
          width={500}
          okText={editingId ? "Update" : "Create"}
          cancelText="Cancel"
        >
          <Form form={form} layout="vertical">
            {listType === "languages" ? (
              <React.Fragment>
                <Form.Item
                  name="name"
                  label="Language Name"
                  rules={[
                    { required: true, message: "Please input language name!" },
                  ]}
                >
                  <Input placeholder="Enter language name" />
                </Form.Item>
                <Form.Item
                  name="code"
                  label="Language Code"
                  rules={[
                    { required: true, message: "Please input language code!" },
                  ]}
                >
                  <Input placeholder="Enter language code (e.g., en, hi, fr)" />
                </Form.Item>
                {/* <Form.Item
                  name="group"
                  label="Language Group"
                  rules={[
                    { required: true, message: "Please input language group!" },
                  ]}
                >
                  <Input placeholder="Enter language group" />
                </Form.Item> */}
              </React.Fragment>
            ) : listType === "groups" ? (
              <React.Fragment>
                <Form.Item
                  name="group_name"
                  label="Group Name"
                  rules={[
                    { required: true, message: "Please input group name!" },
                  ]}
                >
                  <Input placeholder="Enter language group name" />
                </Form.Item>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Form.Item
                  name="language_id"
                  label="Language"
                  rules={[
                    { required: true, message: "Please select a language!" },
                  ]}
                >
                  <Select
                    placeholder="Select a language"
                    showSearch
                    optionFilterProp="children"
                    loading={languagesState.loading === "pending"}
                    style={{ width: "100%" }}
                  >
                    {/* Dynamic options from existing languages */}
                    {languageData?.result?.map((lang: Language) => (
                      <Select.Option key={lang.id} value={lang.id}>
                        {lang.name} ({lang.code})
                      </Select.Option>
                    )) || (
                        // Fallback static options if languages not loaded
                        <>
                          <Select.Option value="1">English (en)</Select.Option>
                          <Select.Option value="2">Hindi (hi)</Select.Option>
                          <Select.Option value="3">French (fr)</Select.Option>
                          <Select.Option value="4">Spanish (es)</Select.Option>
                          <Select.Option value="5">German (de)</Select.Option>
                        </>
                      )}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="version"
                  label="Version"
                  rules={[
                    { required: true, message: "Please input version!" },
                    {
                      pattern: /^[a-zA-Z0-9._-]+$/,
                      message:
                        "Version can only contain letters, numbers, dots, hyphens and underscores",
                    },
                    {
                      min: 1,
                      max: 50,
                      message: "Version must be between 1 and 50 characters",
                    },
                  ]}
                  extra="Examples: v1.0, v2.1.3, 2024.1, release-1.0"
                >
                  <Input
                    placeholder="Enter version (e.g., v1.0, 2024.1)"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </React.Fragment>
            )}
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default LanguageList;
